import React, { useEffect, useState } from "react";
import { Plus, Trash2, RefreshCw, RotateCcw, Copy, Check } from "lucide-react";
import toast from "react-hot-toast";
import api from "../api";

export default function Licenses() {
  const [licenses, setLicenses] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHwidModal, setShowHwidModal] = useState(false);
  const [hwidForm, setHwidForm] = useState({ hwid: "", expire_days: 30, uses: -1, note: "", telegram: "" });
  const [copied, setCopied] = useState("");
  const [form, setForm] = useState({
    expire_days: 30,
    uses: 100,
    note: "",
    custom_key: "",
  });
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/list_licenses");
      setLicenses(res.data);
    } catch (e) {
      toast.error("Gagal load licenses");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createLicense = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/create_license", {
        expire_days: parseInt(form.expire_days),
        uses: parseInt(form.uses),
        note: form.note,
        key: form.custom_key || undefined,
      });
      toast.success(`Key dibuat: ${res.data.key}`);
      setShowModal(false);
      setForm({ expire_days: 30, uses: 100, note: "", custom_key: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal buat license");
    }
  };

  const revoke = async (key) => {
    if (!window.confirm(`Hapus HWID ${key}? (tidak bisa diundo)`)) return;
    try {
      await api.post(`/admin/revoke_license?key=${encodeURIComponent(key)}`);
      toast.success("HWID dihapus");
      load();
    } catch (e) {
      toast.error("Gagal hapus");
    }
  };

  const registerHwid = async (e) => {
    e.preventDefault();
    if (!hwidForm.hwid.trim()) return toast.error("Masukkan HWID");
    try {
      const res = await api.post(
        `/admin/register_hwid?hwid=${encodeURIComponent(hwidForm.hwid)}&expire_days=${hwidForm.expire_days}&uses=${hwidForm.uses}&note=${encodeURIComponent(hwidForm.note)}&telegram=${encodeURIComponent(hwidForm.telegram)}`
      );
      if (res.data.status === "already_registered") {
        toast.error(`HWID sudah terdaftar (key: ${res.data.key})`);
      } else {
        toast.success(`HWID terdaftar! Key: ${res.data.key}`);
        setShowHwidModal(false);
        setHwidForm({ hwid: "", expire_days: 30, uses: -1, note: "", telegram: "" });
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal daftarkan HWID");
    }
  };

  const resetHwid = async (key) => {
    try {
      await api.post(`/admin/reset_hwid?key=${encodeURIComponent(key)}`);
      toast.success("HWID direset");
      load();
    } catch (e) {
      toast.error("Gagal reset HWID");
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopied(key);
    toast.success("Key disalin!");
    setTimeout(() => setCopied(""), 2000);
  };

  const filtered = Object.entries(licenses).filter(([key, lic]) =>
    key.toLowerCase().includes(search.toLowerCase()) ||
    (lic.note || "").toLowerCase().includes(search.toLowerCase())
  );

  const isExpired = (expire) => expire && new Date(expire) < new Date();

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>License Manager</h1>
          <p style={styles.sub}>{Object.keys(licenses).length} total keys</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={load} style={styles.btnSecondary} disabled={loading}>
            <RefreshCw size={15} />
          </button>
          <button onClick={() => setShowHwidModal(true)} style={{ ...styles.btnPrimary, background: "linear-gradient(135deg,#10b981,#059669)" }}>
            <Plus size={16} /> Daftarkan HWID
          </button>
        </div>
      </div>

      {/* Search */}
      <input
        placeholder="Cari key atau catatan..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={styles.search}
      />

      {/* Table */}
      <div style={styles.table}>
        <div style={styles.tableHeader}>
          <span style={{ flex: 3 }}>License Key</span>
          <span style={{ flex: 1 }}>Status</span>
          <span style={{ flex: 1 }}>Uses</span>
          <span style={{ flex: 2 }}>Expire</span>
          <span style={{ flex: 2 }}>Note / HWID</span>
          <span style={{ flex: 2, textAlign: "right" }}>Aksi</span>
        </div>

        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={styles.empty}>Tidak ada license</div>
        ) : (
          filtered.map(([key, lic]) => {
            const expired = isExpired(lic.expire);
            const status = !lic.active ? "REVOKED" : expired ? "EXPIRED" : "AKTIF";
            const statusColor = status === "AKTIF" ? "#4ade80" : "#f87171";

            return (
              <div key={key} style={styles.row}>
                {/* Key */}
                <span style={{ flex: 3, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={styles.keyText}>{key}</span>
                  <button onClick={() => copyKey(key)} style={styles.iconBtn}>
                    {copied === key ? <Check size={13} color="#4ade80" /> : <Copy size={13} />}
                  </button>
                </span>

                {/* Status */}
                <span style={{ flex: 1 }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: statusColor + "22", color: statusColor,
                  }}>
                    {status}
                  </span>
                </span>

                {/* Uses */}
                <span style={{ flex: 1, color: "#94a3b8", fontSize: 13 }}>
                  {lic.uses_left === -1 ? "∞" : lic.uses_left}
                </span>

                {/* Expire */}
                <span style={{ flex: 2, color: expired ? "#f87171" : "#94a3b8", fontSize: 12 }}>
                  {lic.expire ? lic.expire.slice(0, 10) : "Unlimited"}
                </span>

                {/* Note / HWID */}
                <span style={{ flex: 2 }}>
                  {lic.note && <div style={{ color: "#e2e8f0", fontSize: 12 }}>{lic.note}</div>}
                  <div style={{ color: lic.hwid ? "#f59e0b" : "#475569", fontSize: 11 }}>
                    {lic.hwid ? `HWID: ${lic.hwid.slice(0, 12)}...` : "HWID: belum terikat"}
                  </div>
                </span>

                {/* Aksi */}
                <span style={{ flex: 2, display: "flex", justifyContent: "flex-end", gap: 6 }}>
                  <button
                    onClick={() => resetHwid(key)}
                    title="Reset HWID"
                    style={{ ...styles.iconBtn, color: "#f59e0b" }}
                  >
                    <RotateCcw size={15} />
                  </button>
                  {lic.active && (
                    <button
                      onClick={() => revoke(key)}
                      title="Hapus HWID"
                      style={{ ...styles.iconBtn, color: "#f87171" }}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* Modal Daftarkan HWID */}
      {showHwidModal && (
        <div style={styles.overlay} onClick={() => setShowHwidModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>🖥️ Daftarkan HWID Customer</h2>
            <p style={{ color:"#64748b", fontSize:13, margin:"0 0 16px" }}>
              Customer jalankan .exe → muncul HWID → paste di sini
            </p>
            <form onSubmit={registerHwid} style={styles.modalForm}>
              <div style={styles.formGroup}>
                <label style={styles.label}>HWID Customer *</label>
                <input
                  type="text"
                  placeholder="contoh: 7f6e91c886fbd06cf9dda678ce5a9504"
                  value={hwidForm.hwid}
                  onChange={(e) => setHwidForm({ ...hwidForm, hwid: e.target.value })}
                  style={{ ...styles.input, fontFamily:"monospace", fontSize:12 }}
                  autoFocus
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Durasi (hari, 0 = unlimited)</label>
                <input
                  type="number" min="0"
                  value={hwidForm.expire_days}
                  onChange={(e) => setHwidForm({ ...hwidForm, expire_days: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Max Uses (-1 = unlimited)</label>
                <input
                  type="number" min="-1"
                  value={hwidForm.uses}
                  onChange={(e) => setHwidForm({ ...hwidForm, uses: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Catatan (nama customer)</label>
                <input
                  type="text" placeholder="contoh: Budi - 30 hari"
                  value={hwidForm.note}
                  onChange={(e) => setHwidForm({ ...hwidForm, note: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Telegram (username tanpa @)</label>
                <input
                  type="text" placeholder="contoh: budisantoso"
                  value={hwidForm.telegram}
                  onChange={(e) => setHwidForm({ ...hwidForm, telegram: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={{ display:"flex", gap:10, marginTop:8 }}>
                <button type="button" onClick={() => setShowHwidModal(false)} style={styles.btnSecondary2}>
                  Batal
                </button>
                <button type="submit" style={{ ...styles.btnPrimary, flex:1, justifyContent:"center", background:"linear-gradient(135deg,#10b981,#059669)" }}>
                  ✓ Daftarkan HWID
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Buat Key */}
      {showModal && (
        <div style={styles.overlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Buat License Key Baru</h2>
            <form onSubmit={createLicense} style={styles.modalForm}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Durasi (hari, 0 = unlimited)</label>
                <input
                  type="number" min="0" value={form.expire_days}
                  onChange={(e) => setForm({ ...form, expire_days: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Max Uses (-1 = unlimited)</label>
                <input
                  type="number" min="-1" value={form.uses}
                  onChange={(e) => setForm({ ...form, uses: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Catatan (nama customer)</label>
                <input
                  type="text" placeholder="contoh: Budi - 30 hari"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Custom Key (kosongkan = auto)</label>
                <input
                  type="text" placeholder="LIC-XXXX-XXXX"
                  value={form.custom_key}
                  onChange={(e) => setForm({ ...form, custom_key: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <button type="button" onClick={() => setShowModal(false)} style={styles.btnSecondary2}>
                  Batal
                </button>
                <button type="submit" style={styles.btnPrimary}>
                  <Plus size={15} /> Buat Key
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: "32px 32px 32px 252px", minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { color: "#fff", fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: "#64748b", fontSize: 13, marginTop: 4 },
  btnPrimary: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "9px 18px", background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  btnSecondary: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "9px 12px", background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    color: "#94a3b8", fontSize: 13, cursor: "pointer",
  },
  btnSecondary2: {
    flex: 1, padding: "10px 0", background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    color: "#94a3b8", fontSize: 14, cursor: "pointer",
  },
  search: {
    width: "100%", maxWidth: 360, padding: "9px 14px",
    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, color: "#fff", fontSize: 13, outline: "none",
    marginBottom: 16, boxSizing: "border-box",
  },
  table: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12 },
  tableHeader: {
    display: "flex", padding: "12px 20px",
    color: "#64748b", fontSize: 11, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: 0.5,
    borderBottom: "1px solid rgba(255,255,255,0.07)",
  },
  row: {
    display: "flex", padding: "13px 20px", alignItems: "center",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
  },
  keyText: { fontFamily: "monospace", fontSize: 12, color: "#a5b4fc" },
  iconBtn: {
    background: "none", border: "none", color: "#64748b",
    cursor: "pointer", padding: 4, display: "flex", alignItems: "center",
  },
  empty: { padding: "32px", textAlign: "center", color: "#64748b" },
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
  },
  modal: {
    background: "#1e2130", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 14, padding: "32px", width: 420,
    boxShadow: "0 25px 50px rgba(0,0,0,0.6)",
  },
  modalTitle: { color: "#fff", fontSize: 18, fontWeight: 700, margin: "0 0 20px" },
  modalForm: { display: "flex", flexDirection: "column", gap: 14 },
  formGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: { color: "#94a3b8", fontSize: 12, fontWeight: 500 },
  input: {
    padding: "10px 12px", background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    color: "#fff", fontSize: 13, outline: "none",
  },
};
